// Port of Flutter's fanTouchScreen.dart (DeviceID 53).
//
// A 270° dial with the gap at the BOTTOM — it starts at 135° (down-left) and
// sweeps clockwise to 45° (down-right) — with the spinning blades in the middle
// and the speed readout beneath the hub.
//
// Two behaviours worth keeping in mind, both lifted from the Dart:
//
//  • Dragging moves the UI ONLY. Exactly one command is published, on release.
//    The older slider published on every drag tick and machine-gunned the device
//    with MQTT frames for a single gesture.
//  • The hub area (inner 22%) is dead to touch, so grabbing the fan graphic
//    itself doesn't fling the speed.
//
// Both now live in ui/shared/controls/FanDialControl (2026-10-03), which the
// desktop fan card renders too — one dial, one set of steps, one command, on
// every shell. This screen owns only the frame: app bar, sizing, long-press.
//
// Long-pressing the body opens the schedule for output 1, as in the Dart.

import React, { useMemo } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useContentHeight } from '../../components/MobileShell';
import DeviceAppBar from './components/DeviceAppBar';
import FanDialControl from '../../../shared/controls/FanDialControl';
import useLongPress from '../../components/useLongPress';

export default function FanScreen({ device }) {
  const theme = useTheme();
  const contentH = useContentHeight();
  const c = theme.sli;
  const navigate = useNavigate();
  const uno = device?.UniqueNumber;

  const box = useMemo(() => {
    const availW = window.innerWidth;
    // ⚠️ THE CONTENT AREA, NOT THE WINDOW. The window also holds the identity
    // strip and the company footer, which this screen does not get — sizing
    // from it overflows by exactly their height, and the overflow is clipped
    // rather than scrolled. See ui/mobile/components/MobileShell.
    const availH = contentH - 64;
    return Math.min(380, Math.max(220, Math.min(availW * 0.82, availH * 0.62)));
  }, [contentH]);

  const bodyPress = useLongPress(
    () => navigate(`/schedule/${uno}/1`, { state: { outputName: device?.DeviceName, device } }),
    null,
  );

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', bgcolor: c.back, display: 'flex', flexDirection: 'column' }}>
      <DeviceAppBar device={device} />

      <Box
        {...bodyPress}
        sx={{
          flex: 1, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', p: 2,
        }}
      >
        <FanDialControl device={device} size={box} />

        <Box sx={{ height: box * 0.05 }} />
        <Typography sx={{
          fontSize: Math.min(15, Math.max(11, box * 0.045)), color: `${c.text}99`,
        }}
        >
          Drag around the dial to set speed
        </Typography>
      </Box>
    </Box>
  );
}
